import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  closeOnBackdrop?: boolean;
}

const sizeMap = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Keep onClose in a ref so the key-handler effect doesn't have to depend on
  // (and re-run every time) the caller's inline arrow. Re-running that effect
  // on every render would refocus the dialog and steal focus from inputs.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Mount/unmount-only: lock body scroll, move focus to the dialog once, and
  // restore focus to whatever opened the modal when it closes.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    requestAnimationFrame(() => dialogRef.current?.focus());
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Keyboard: Esc closes, Tab traps focus inside the dialog. Stable across
  // re-renders because it reads onClose via the ref above.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusable = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={description ? 'modal-description' : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-xl bg-bg-surface-2 border border-border-strong shadow-2xl outline-none',
          sizeMap[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-3">
          <div className="min-w-0 flex-1">
            <h2 id="modal-title" className="text-xl font-semibold text-text-primary leading-tight">
              {title}
            </h2>
            {description && (
              <p id="modal-description" className="mt-1 text-sm text-text-secondary">
                {description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close dialog" className="px-2 -mr-2">
            <X className="size-4" aria-hidden />
          </Button>
        </header>
        {children && <div className="px-6 py-3">{children}</div>}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 pt-3 pb-5 border-t border-border-subtle mt-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
